/**
 * PoiGestionPage — gestion mairie « Points d'intérêt / Tourisme & Entraide » (GRC).
 *
 * Brique VISUELLE à part (header dédié), rattachée au service GRC.
 * - Carte Leaflet+IGN : clic = pose un marqueur → coordonnées.
 * - Liste filtrable par famille, modale création/édition, upload photo (1),
 *   (dés)affichage, suppression (maire/adjoint).
 * - Lien « Planning de l'équipe » → planning GRC (pas de planning dupliqué).
 *
 * Conforme au guide : AppHeader (← Dashboard), zéro window.confirm
 * (useConfirmDialog + {dialogElement}), upload via FormData.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '@/components/AppHeader';
import { Card, Button, Badge, Alert, Input, SectionEyebrow } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { useAuth } from '@/lib/auth-context';
import { PoiMap } from '@/components/poi/PoiMap';
import {
  POI_FAMILLE_LABEL, POI_FAMILLE_COLOR, POI_TYPES_PAR_FAMILLE, POI_TYPE_LABEL, POI_TYPE_EMOJI,
  type PoiFamille,
} from '@/components/poi/poiTypes';
import { compressImageIfNeeded } from '@/lib/image-compress';
import { api, ApiException } from '@/lib/api';
import type { PoiPoint, PoiInput } from '@/lib/api';
import {
  Plus, Pencil, Trash2, AlertCircle, MapPin, Upload, Calendar, Eye, EyeOff, ExternalLink, Download, QrCode, Copy, Check,
} from 'lucide-react';

type PoiForm = {
  famille: PoiFamille; type: string; nom: string; description: string;
  adresse: string; horaires: string; note: string; telephone: string; siteWeb: string;
  affiche: boolean; latitude: number | null; longitude: number | null;
};

const emptyForm: PoiForm = {
  famille: 'securite', type: 'dae', nom: '', description: '', adresse: '',
  horaires: '', note: '', telephone: '', siteWeb: '', affiche: true,
  latitude: null, longitude: null,
};

const FAMILLES: PoiFamille[] = ['securite', 'entraide', 'tourisme'];

export function PoiGestionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirm, dialogElement } = useConfirmDialog();

  const [points, setPoints] = useState<PoiPoint[]>([]);
  const [filtreFamille, setFiltreFamille] = useState<PoiFamille | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<PoiForm>(emptyForm);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [catalogue, setCatalogue] = useState<Array<{ famille: string; sousCategorie: string; label: string; type: string }>>([]);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [rayonKm, setRayonKm] = useState<number>(15);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [affichetteOpen, setAffichetteOpen] = useState(false);
  const [pageUrl, setPageUrl] = useState('');
  const [copie, setCopie] = useState(false);

  async function ouvrirAffichette() {
    try {
      const r = await api.grc.poi.affichetteUrl();
      setPageUrl(r.pageUrl);
      setAffichetteOpen(true);
      setCopie(false);
    } catch (e) {
      setErr(e instanceof ApiException ? e.message : "Impossible de préparer l'affichette.");
    }
  }

  async function copierLien() {
    try { await navigator.clipboard.writeText(pageUrl); setCopie(true); setTimeout(() => setCopie(false), 2000); }
    catch { /* clipboard indisponible : l'utilisateur copie manuellement */ }
  }

  async function telechargerAffichette() {
    try {
      const blob = await api.grc.poi.affichettePdfBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'affichette-points-interet.pdf';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e instanceof ApiException ? e.message : 'Téléchargement impossible.');
    }
  }

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  async function ouvrirImport() {
    setImportMsg(null);
    try {
      const r = await api.grc.poi.importCatalogue();
      setCatalogue(r.catalogue);
      if (typeof r.rayonKm === 'number') setRayonKm(r.rayonKm);
      // Tout coché par défaut (la mairie décoche ce qu'elle ne veut pas).
      setSelection(new Set(r.catalogue.map((c) => c.sousCategorie)));
      setImportOpen(true);
    } catch (e) {
      setErr(e instanceof ApiException ? e.message : 'Impossible de charger les catégories.');
    }
  }

  function toggleSel(sc: string) {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(sc)) next.delete(sc); else next.add(sc);
      return next;
    });
  }

  async function lancerImport() {
    setImporting(true); setImportMsg(null);
    try {
      const sel = Array.from(selection);
      const r = await api.grc.poi.importGeotag(sel.length > 0 ? sel : undefined, rayonKm);
      await reload();
      setImportMsg(`Import terminé : ${r.crees} créé·s, ${r.maj} mis à jour, ${r.ignores} ignoré·s (sur ${r.total} lieux reçus).`);
    } catch (e) {
      setImportMsg(e instanceof ApiException ? e.message : 'Import GeoTag impossible.');
    } finally { setImporting(false); }
  }

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.grc.poi.list(filtreFamille === 'all' ? undefined : filtreFamille);
      setPoints(r.points);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiException ? e.message : 'Chargement impossible.');
    } finally { setLoading(false); }
  }, [filtreFamille]);

  useEffect(() => { void reload(); }, [reload]);

  function ouvrirCreation() {
    setEditId(null); setForm(emptyForm); setPhotoFile(null); setOpen(true);
  }
  function ouvrirEdition(p: PoiPoint) {
    setEditId(p.id);
    setForm({
      famille: p.famille, type: p.type, nom: p.nom, description: p.description ?? '',
      adresse: p.adresse ?? '', horaires: p.horaires ?? '', note: p.note ?? '',
      telephone: p.telephone ?? '', siteWeb: p.siteWeb ?? '', affiche: p.affiche,
      latitude: p.latitude, longitude: p.longitude,
    });
    setPhotoFile(null); setOpen(true);
  }

  function onPickCoords(c: { lat: number; lng: number }) {
    setForm((f) => ({ ...f, latitude: c.lat, longitude: c.lng }));
    if (!open) { /* on ouvre la création si aucune modale ouverte */ ouvrirCreation(); setForm((f) => ({ ...emptyForm, latitude: c.lat, longitude: c.lng })); }
  }

  function changerFamille(fam: PoiFamille) {
    // Réaligne le type sur le 1er de la nouvelle famille.
    setForm((f) => ({ ...f, famille: fam, type: POI_TYPES_PAR_FAMILLE[fam][0] ?? 'autre' }));
  }

  async function enregistrer() {
    if (form.nom.trim().length < 2) { setErr('Le nom est requis (2 caractères min).'); return; }
    setSaving(true); setErr(null);
    try {
      const body: PoiInput = {
        famille: form.famille, type: form.type, nom: form.nom.trim(),
        description: form.description.trim() || undefined,
        adresse: form.adresse.trim() || undefined,
        horaires: form.horaires.trim() || undefined,
        note: form.note.trim() || undefined,
        telephone: form.telephone.trim() || undefined,
        siteWeb: form.siteWeb.trim() || undefined,
        affiche: form.affiche,
        latitude: form.latitude ?? undefined,
        longitude: form.longitude ?? undefined,
      };
      const saved = editId
        ? await api.grc.poi.update(editId, body)
        : await api.grc.poi.create(body);
      if (photoFile) {
        const prep = await compressImageIfNeeded(photoFile);
        await api.grc.poi.photo(saved.id, prep);
      }
      setOpen(false);
      await reload();
    } catch (e) {
      setErr(e instanceof ApiException ? e.message : 'Enregistrement impossible.');
    } finally { setSaving(false); }
  }

  async function toggleAffiche(p: PoiPoint) {
    try { await api.grc.poi.update(p.id, { affiche: !p.affiche }); await reload(); }
    catch { setErr('Modification impossible.'); }
  }

  async function supprimer(p: PoiPoint) {
    const ok = await confirm({
      title: 'Supprimer ce point ?',
      description: `« ${p.nom} » sera définitivement retiré de la carte.`,
      confirmLabel: 'Supprimer', danger: true,
    });
    if (!ok) return;
    try { await api.grc.poi.remove(p.id); await reload(); }
    catch { setErr('Suppression impossible.'); }
  }

  const pointsFiltres = filtreFamille === 'all' ? points : points.filter((p) => p.famille === filtreFamille);

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Points d'intérêt" accent="grc" />
      <main id="main-content" className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <SectionEyebrow>GRC · Tourisme &amp; Entraide</SectionEyebrow>
            <h1 className="mt-1 text-2xl font-semibold">Points d'intérêt</h1>
            <p className="mt-1 text-sm text-ink-subtle">
              Posez sur la carte les points utiles de la commune : sécurité, entraide, tourisme.
              Cliquez la carte pour ajouter un point.
            </p>
          </div>
          {/* flex-wrap : sur mobile les boutons (dont « Nouveau point ») passent
              à la ligne au lieu de déborder hors cadre à droite. */}
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => navigate('/grc/planning')}>
              <Calendar size={15} className="mr-1.5" /> Planning de l'équipe
            </Button>
            {isAdmin && (
              <Button variant="secondary" size="sm" onClick={() => void ouvrirImport()} disabled={importing}>
                <Download size={15} className="mr-1.5" /> Importer GeoTag
              </Button>
            )}
            {isAdmin && (
              <Button variant="secondary" size="sm" onClick={() => void ouvrirAffichette()}>
                <QrCode size={15} className="mr-1.5" /> QR / Affichette
              </Button>
            )}
            <Button size="sm" onClick={ouvrirCreation}>
              <Plus size={15} className="mr-1.5" /> Nouveau point
            </Button>
          </div>
        </div>

        {err && <Alert variant="error" className="mb-4">{err}</Alert>}

        {/* Filtres familles */}
        <div className="mb-4 flex flex-wrap gap-2">
          <FamChip active={filtreFamille === 'all'} onClick={() => setFiltreFamille('all')} label="Tout" />
          {FAMILLES.map((fam) => (
            <FamChip key={fam} active={filtreFamille === fam} onClick={() => setFiltreFamille(fam)}
              label={POI_FAMILLE_LABEL[fam]} color={POI_FAMILLE_COLOR[fam]} />
          ))}
          <span className="ml-auto self-center text-xs text-ink-muted">{pointsFiltres.length} point·s</span>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
          {/* Carte */}
          <Card className="overflow-hidden p-0">
            <PoiMap points={pointsFiltres} height={440}
              onPick={onPickCoords}
              draft={form.latitude != null && form.longitude != null ? { lat: form.latitude, lng: form.longitude } : null} />
          </Card>

          {/* Liste */}
          <div className="flex max-h-[440px] flex-col gap-2 overflow-y-auto pr-1">
            {loading ? (
              <p className="text-sm text-ink-subtle">Chargement…</p>
            ) : pointsFiltres.length === 0 ? (
              <Card className="p-4 text-sm text-ink-subtle">
                Aucun point pour l'instant. Cliquez sur la carte ou « Nouveau point » pour commencer.
              </Card>
            ) : pointsFiltres.map((p) => (
              <Card key={p.id} className="p-3">
                <div className="flex items-start gap-2.5">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-base"
                    style={{ background: POI_FAMILLE_COLOR[p.famille] + '22' }}>
                    {POI_TYPE_EMOJI[p.type] ?? '📍'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold">{p.nom}</span>
                      {!p.affiche && <Badge variant="neutral" size="sm">masqué</Badge>}
                    </div>
                    <div className="text-[11px] text-ink-muted">
                      {POI_FAMILLE_LABEL[p.famille]} · {POI_TYPE_LABEL[p.type] ?? p.type}
                    </div>
                    {p.adresse && <div className="mt-0.5 truncate text-[11px] text-ink-subtle">{p.adresse}</div>}
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => ouvrirEdition(p)}>
                    <Pencil size={13} className="mr-1" /> Éditer
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => void toggleAffiche(p)}>
                    {p.affiche ? <><EyeOff size={13} className="mr-1" /> Masquer</> : <><Eye size={13} className="mr-1" /> Afficher</>}
                  </Button>
                  {isAdmin && (
                    <Button variant="ghost" size="sm" className="ml-auto text-danger-fg" onClick={() => void supprimer(p)}>
                      <Trash2 size={13} />
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </main>

      {/* Modale création / édition */}
      <Dialog open={open} onClose={() => setOpen(false)} title={editId ? 'Modifier le point' : 'Nouveau point'}>
        <div className="space-y-3">
          {form.latitude != null && form.longitude != null ? (
            <p className="flex items-center gap-1.5 text-xs text-ink-subtle">
              <MapPin size={13} /> Position : {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
            </p>
          ) : (
            <Alert variant="info">Cliquez sur la carte pour positionner le point (recommandé).</Alert>
          )}

          <Lbl label="Nom du point">
            <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })}
              placeholder="Défibrillateur — Place de l'Église" />
          </Lbl>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-subtle">Famille</label>
            <div className="grid grid-cols-3 gap-1.5">
              {FAMILLES.map((fam) => (
                <button key={fam} type="button" onClick={() => changerFamille(fam)}
                  className={`rounded-lg border px-2 py-1.5 text-xs ${form.famille === fam ? 'border-transparent text-ink' : 'border-border text-ink-subtle'}`}
                  style={form.famille === fam ? { background: POI_FAMILLE_COLOR[fam] + '22' } : {}}>
                  {POI_FAMILLE_LABEL[fam]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-subtle">Type</label>
            <div className="grid grid-cols-2 gap-1.5">
              {[...POI_TYPES_PAR_FAMILLE[form.famille], 'autre'].map((t) => (
                <button key={t} type="button" onClick={() => setForm({ ...form, type: t })}
                  className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs ${form.type === t ? 'border-grc bg-grc-tint text-grc-fg' : 'border-border text-ink-subtle'}`}>
                  <span>{POI_TYPE_EMOJI[t] ?? '📍'}</span> {POI_TYPE_LABEL[t] ?? t}
                </button>
              ))}
            </div>
          </div>

          <Lbl label="Description / infos pratiques">
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="min-h-[56px] w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
              placeholder="Accès libre 24h/24, mur extérieur côté parking." />
          </Lbl>

          <div className="grid grid-cols-2 gap-2">
            <Lbl label="Adresse"><Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} /></Lbl>
            <Lbl label="Horaires"><Input value={form.horaires} onChange={(e) => setForm({ ...form, horaires: e.target.value })} placeholder="Accès libre" /></Lbl>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Lbl label="Téléphone (option)"><Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} /></Lbl>
            <Lbl label="Site web (option)"><Input value={form.siteWeb} onChange={(e) => setForm({ ...form, siteWeb: e.target.value })} placeholder="https://" /></Lbl>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-subtle">Photo (option, 1 max)</label>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs text-ink hover:border-grc/50">
              <Upload size={14} /> {photoFile ? photoFile.name : 'Choisir une image'}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)} />
            </label>
          </div>

          <label className="flex items-center justify-between border-t border-border pt-3 text-sm">
            <span>Visible par les habitants</span>
            <input type="checkbox" checked={form.affiche} onChange={(e) => setForm({ ...form, affiche: e.target.checked })} />
          </label>

          <div className="flex gap-2 pt-1">
            <Button className="flex-1" onClick={() => void enregistrer()} disabled={saving}>
              {saving ? 'Enregistrement…' : editId ? 'Enregistrer' : 'Créer le point'}
            </Button>
            <Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
          </div>

          <p className="flex items-start gap-1.5 rounded-lg border border-dashed border-border bg-surface-hi px-2.5 py-2 text-[11px] text-ink-muted">
            <ExternalLink size={12} className="mt-0.5 shrink-0" />
            Origine : commune. Les points touristiques importés de GeoTag apparaîtront ici plus tard, en lecture seule.
          </p>
        </div>
      </Dialog>

      {/* Modale : sélection des catégories à importer depuis GeoTag */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} title="Importer depuis GeoTag">
        <div className="space-y-3">
          <p className="text-sm text-ink-subtle">
            Choisissez les types de lieux à importer sur votre carte. Les lieux non
            cochés (commerces, banques, restaurants…) sont ignorés.
          </p>

          <div className="rounded-lg border border-border p-3">
            <label htmlFor="rayon-import" className="block text-xs font-semibold text-ink">
              Rayon de recherche autour de la commune
            </label>
            <div className="mt-1.5 flex items-center gap-3">
              <input id="rayon-import" type="range" min={1} max={50} step={1}
                value={rayonKm} onChange={(e) => setRayonKm(Number(e.target.value))}
                className="flex-1 accent-[oklch(var(--c-grc,var(--c-primary)))]" />
              <span className="w-16 text-right text-sm font-medium text-ink">{rayonKm} km</span>
            </div>
            <p className="mt-1 text-[11px] text-ink-muted">
              Pour une petite commune, élargissez (15-25 km) pour capter les lieux alentour.
            </p>
          </div>

          {(['securite', 'tourisme', 'entraide'] as const).map((fam) => {
            const items = catalogue.filter((c) => c.famille === fam);
            if (items.length === 0) return null;
            return (
              <div key={fam} className="rounded-lg border border-border p-3">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold" style={{ color: POI_FAMILLE_COLOR[fam] }}>
                  <span className="h-2 w-2 rounded-full" style={{ background: POI_FAMILLE_COLOR[fam] }} />
                  {POI_FAMILLE_LABEL[fam]}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {items.map((c) => (
                    <label key={c.sousCategorie} className="inline-flex items-center gap-2 text-xs text-ink">
                      <input type="checkbox" checked={selection.has(c.sousCategorie)}
                        onChange={() => toggleSel(c.sousCategorie)}
                        className="h-3.5 w-3.5 accent-[oklch(var(--c-grc,var(--c-primary)))]" />
                      {c.label}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}

          {importMsg && <Alert variant={importMsg.includes('terminé') ? 'info' : 'error'}>{importMsg}</Alert>}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" size="sm" onClick={() => setImportOpen(false)}>Fermer</Button>
            <Button size="sm" onClick={() => void lancerImport()} disabled={importing || selection.size === 0}>
              {importing ? 'Import en cours…' : `Importer (${selection.size})`}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Modale : QR code / affichette de la page publique */}
      <Dialog open={affichetteOpen} onClose={() => setAffichetteOpen(false)} title="QR code & affichette">
        <div className="space-y-4">
          <p className="text-sm text-ink-subtle">
            Diffusez la carte des points d'intérêt de votre commune : le public y accède
            sans compte, en scannant le QR code ou via le lien ci-dessous.
          </p>

          <div>
            <div className="text-xs font-semibold text-ink">Lien public de la commune</div>
            <div className="mt-1.5 flex items-center gap-2">
              <input readOnly value={pageUrl}
                className="min-w-0 flex-1 rounded-lg border border-border bg-surface px-3 py-2 text-xs text-ink-subtle" />
              <Button variant="secondary" size="sm" onClick={() => void copierLien()}>
                {copie ? <Check size={14} /> : <Copy size={14} />}
                <span className="ml-1.5">{copie ? 'Copié' : 'Copier'}</span>
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-grc/30 bg-grc-tint/40 p-3">
            <div className="text-sm font-semibold text-ink">Affichette prête à imprimer</div>
            <p className="mt-1 text-[11px] text-ink-subtle">
              Un PDF A4 à l'en-tête de votre mairie, avec un grand QR code. Idéal pour un
              flyer, un panneau ou l'office de tourisme. Le QR mène à la carte (qui contient
              aussi l'accès à GeoTag pour explorer les environs).
            </p>
            <div className="mt-2.5 flex justify-end">
              <Button size="sm" onClick={() => void telechargerAffichette()}>
                <Download size={15} className="mr-1.5" /> Télécharger l'affichette PDF
              </Button>
            </div>
          </div>
        </div>
      </Dialog>

      {dialogElement}
    </div>
  );
}

function FamChip({ active, onClick, label, color }: { active: boolean; onClick: () => void; label: string; color?: string }) {  return (
    <button type="button" onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs ${active ? 'border-transparent text-ink' : 'border-border text-ink-subtle'}`}
      style={active && color ? { background: color + '22' } : active ? { background: 'var(--surface-2)' } : {}}>
      {color && <span className="h-2 w-2 rounded-full" style={{ background: color }} />}
      {label}
    </button>
  );
}

function Lbl({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-ink-subtle">{label}</label>
      {children}
    </div>
  );
}
